import type React from 'react';
import { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { size } from '../../layout/helpers';
import { ButtonReset } from '../../layout/reset';
import { Wrapper as Chat } from '../Chat/wrapper';

export const animationDuration = 200;

type WrapperProps = React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
> & {
  $fullSize?: boolean;
  $chat?: boolean;
  $size?: 'narrow' | 'small' | 'wide' | 'extraWide';
};

const WrapperComponent = forwardRef<HTMLDivElement, WrapperProps>((props, ref) => {
  const { $fullSize, $chat, $size, ...rest } = props;
  return <div ref={ref} {...rest} />;
});

export const Wrapper = styled(WrapperComponent).attrs(() => ({
  role: 'dialog',
  'aria-modal': 'true',
}))<WrapperProps>`
  --modal-max-width: 700px;
  --modal-gutter: ${size(3)};

  ${(props) => props.theme.breakpoint.Sm} {
    --modal-gutter: ${size(3.5)};
  }

  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  white-space: normal;
  text-align: left;
  z-index: 90;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--c-text-main);

  ${(props) => props.theme.breakpoint.Sm} {
    padding: ${size(2)};
  }

  ${({ $fullSize }) =>
    $fullSize &&
    css`
      flex-direction: column;
    `}

  ${({ $chat }) =>
    $chat &&
    css`
      ${Dialog} {
        display: flex;
        flex-direction: column;
        max-height: min(900px, 100%);
      }

      ${Content} {
        display: flex;
        flex-direction: column;
        overflow: hidden;
      }

      ${Header} {
        flex-shrink: 0;
      }

      ${Body} {
        flex: 1 1 auto;
        display: flex;
        flex-direction: column;
        overflow: hidden;
      }
    `}

    ${({ $size }) =>
      $size === 'narrow'
        ? css`
          --modal-max-width: 400px;
        `
        : $size === 'small'
          ? css`
            --modal-max-width: 550px;
          `
          : $size === 'wide'
            ? css`
              --modal-max-width: 1200px;
            `
            : $size === 'extraWide'
              ? css`
              --modal-max-width: 1600px;
            `
              : undefined}
`;

export const UiBlock = styled.div.attrs(() => ({
  'aria-hidden': 'true',
  role: 'button',
}))`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(0, 0, 0, 0.35);
  transition: opacity ${animationDuration}ms ease;
  z-index: -1;

  .modal-appear & {
    opacity: 0;
  }

  .modal-appear-active & {
    opacity: 1;
  }

  .modal-exit & {
    opacity: 1;
  }

  .modal-exit-active & {
    opacity: 0;
  }
`;

type DialogProps = React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
  $fullSize?: boolean;
};

export const Dialog = styled.div<DialogProps>`
  --_animation-offset-y: 100%;

  display: flex;
  flex-direction: column;
  background-color: var(--c-bg-main);
  z-index: 1;
  width: 100%;
  max-width: var(--modal-max-width);
  max-height: 100%;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  transition:
    transform ${animationDuration}ms ease,
    opacity ${animationDuration}ms ease;

  .modal-appear & {
    transform: translateY(var(--_animation-offset-y));
    opacity: 0;
  }

  .modal-appear-active & {
    transform: translateY(0);
    opacity: 1;
  }

  .modal-exit & {
    transform: translateY(0);
    opacity: 1;
  }

  .modal-exit-active & {
    transform: translateY(var(--_animation-offset-y));
    opacity: 0;
  }

  ${(props) => props.theme.breakpoint.LtSm} {
    align-self: flex-end;
    border-top-left-radius: ${size(3)};
    border-top-right-radius: ${size(3)};
  }

  ${(props) => props.theme.breakpoint.Sm} {
    --_animation-offset-y: ${size(2)};

    border-radius: ${size(1)};
  }

  ${({ $fullSize }) =>
    $fullSize &&
    css`
      flex: 1 1 auto;
      max-width: 100%;
    `}
`;

type ContentProps = React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
> & {
  $fullSize?: boolean;
};

export const Content = styled.div<ContentProps>`
  flex: 1 1 auto;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;

  ${({ $fullSize }) =>
    $fullSize &&
    css`
      display: flex;
      flex-direction: column;
    `}
`;

export const Header = styled.header`
  position: relative;
  display: flex;
  align-items: center;
  position: sticky;
  top: 0;
  padding: var(--modal-gutter);
  padding-bottom: 0;
  background-color: var(--c-bg-main);
  z-index: 10;

  :after {
    content: '';
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    height: var(--modal-gutter);
    background-color: var(--c-bg-main);
    mask-image: ${(props) => props.theme.mask.gradient.toBottom};
  }
`;

export const Heading = styled.div`
  flex: 1 1 auto;

  h1 {
    font-size: 1.5rem;
  }
`;

export const Close = styled.div`
  --close-btn-padding: ${size(2)};

  flex-shrink: 0;
  margin: calc(var(--close-btn-padding) * -1);
  padding-left: var(--modal-gutter);
`;

export const CloseBtn = styled(ButtonReset)`
  --_backdrop-size: ${size(5)};

  position: relative;
  display: block;
  padding: var(--close-btn-padding);
  font-size: ${size(3)};
  transform-origin: 50% 50%;
  transition: transform 200ms ease;
  z-index: 2;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: var(--_backdrop-size);
    height: var(--_backdrop-size);
    border-radius: 50%;
    background-color: var(--c-accent-bg);
    opacity: 0;
    transform: translate(-50%, -50%);
    transition: opacity 200ms ease;
    z-index: -1;
  }

  &:active {
    transform: scale(0.9);
  }

  &:hover {
    &::before {
      opacity: 1;
    }
  }
`;

type BodyProps = React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> & {
  $fullSize?: boolean;
};

export const Body = styled.div<BodyProps>`
  padding: var(--modal-gutter);

  ${({ $fullSize }) =>
    $fullSize &&
    css`
      flex: 1 1 auto;
    `}
`;

export const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${size(0.5)};
  padding: 0 var(--modal-gutter) var(--modal-gutter) var(--modal-gutter);
`;

export const ChatBody = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin: 0 ${size(-3)};
  border-top: 1px solid var(--c-border-main);

  ${Chat} {
    flex: 1 1 auto;
    padding: ${size(2)} ${size(3)};
  }

  ${(props) => props.theme.breakpoint.Sm} {
    margin: 0 ${size(-5)};

    ${Chat} {
      padding-left: ${size(5)};
      padding-right: ${size(5)};
    }
  }
`;

export const ChatFooter = styled.div`
  flex-shrink: 0;
  border-top: 1px solid var(--c-border-main);
  margin: 0 ${size(-3)} ${size(-3)} ${size(-3)};
  padding: ${size(2)} ${size(3)};

  ${(props) => props.theme.breakpoint.Sm} {
    margin: 0 ${size(-5)} ${size(-5)} ${size(-5)};
    padding: ${size(2)} ${size(5)};
  }
`;
